// app/[symbol]/page.tsx
'use client'

import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react'
import { useParams, useRouter } from 'next/navigation'
import GridLayout from 'react-grid-layout'
// Import the useBrokerSelection hook instead of individual components
import { useBrokerSelection } from '../components/Connectors/hooks/use-broker-selection'
import PluginTabs from '../components/PluginTabs'
import AdvancedChart from '../components/AdvancedChart/index'
import Image from 'next/image'
import { Permanent_Marker } from 'next/font/google'
import ScriptEditor from '../components/Plugins/ScriptEditor/ScriptEditor'

import styles from './symbolPage.module.css'
import Backtester from '../components/Plugins/Backtester/Backtester'
import OptionsBuilder from '../components/Plugins/OptionsBuilder/OptionsBuilder'
import Events from '../(pluginPages)/event-alerts/[symbol]/page'
import Sentiment from '../components/Plugins/Sentiment/Sentiment'
import Notes from '../components/Plugins/Notes/Notes'
import NotesPage from '../(pluginPages)/notes/page'
import Hedging from '../components/Plugins/Hedging/Hedging'
import Historicals from '../components/Plugins/Historicals/Historicals'
import Presence from '../components/Presence/Presence'
import AIHedgeFund from '../components/Plugins/AIHedgeFund/AIHedgeFund'; // Import the new component
import AutomationsModal from '../components/Plugins/Automations/AutomationsModal'
import Baskets from '../components/Plugins/Baskets/Baskets'
import AITerminal from '../components/AITerminal/AITerminal'
// Import the OnboardingTutorial component
import OnboardingTutorial from '../components/OnboardingTutorial/OnboardingTutorial'
import TradingPageWrapper from '../components/AITerminal/TradingPageWrapper'
import VisualDemo from '../components/AITerminal/VisualDemo'
import { AuthStatus } from '../components/Auth/AuthStatus'

// Import QueryProvider for performance optimization
import QueryProvider from '../components/Connectors/providers/QueryProvider'
import { 
  useChartData, 
  useLayoutPersistence, 
  useUserScript, 
  useDrawerStates,
  useGridManagement,
  useEventListeners,
  useDragAndDrop,
  useSymbolManagement
} from '../components/TradingPage/hooks/useTradingPageQueries'

/** ========== 1) Types for RGL ========== */
interface RGLLayoutItem {
  i: string
  x: number
  y: number
  w: number
  h: number
  static?: boolean
  minW?: number
  maxW?: number
  minH?: number
  maxH?: number
  moved?: boolean
  isDraggable?: boolean
  isResizable?: boolean
}

type RGLLayout = RGLLayoutItem[]

/** ========== 2) Default Layout ========== */
const defaultLayout: RGLLayout = [
  { i: 'chartArea', x: 0, y: 0, w: 8, h: 16 },
  { i: 'tabsAreaTop', x: 0, y: 10, w: 8, h: 20 },
  { i: 'orderBook', x: 8, y: 0, w: 2, h: 12 },
  { i: 'watchlist', x: 10, y: 0, w: 2, h: 12 },
  { i: 'buySell', x: 8, y: 6, w: 4, h: 12 },
  { i: 'notes', x: 8, y: 24, w: 4, h: 12 }
  // { i: 'tabsAreaBottom', x: 0, y: 14, w: 12, h: 4 }
]

const permanentMarker = Permanent_Marker({
  weight: '400',
  subsets: ['latin'],
  display: 'swap',
  variable: '--font-permanent-marker'
})

export default function SymbolPage() {
  return (
    <AITerminal
      roomId="main-terminal"
      userId="user-123"
      userName="Trader"
    >
      <QueryProvider>
        <SymbolPageContent />
      </QueryProvider>
    </AITerminal>
  )
}

// Internal component with all the existing logic
function SymbolPageContent() {
  /** ========== Symbol Param ========== */
  const routeParams = useParams() as { symbol?: string }
  const initialSymbol = (routeParams.symbol ?? 'BTCUSDT').toUpperCase()
  
  /** ========== Broker Selection ========== */
  const { OrderBookComponent, TerminalTabsComponent, WatchlistComponent, BuySellComponent } = useBrokerSelection()

  /** ========== Optimized Symbol Management ========== */
  const { selectedSymbol, updateSymbol } = useSymbolManagement(initialSymbol)
  const symbol = selectedSymbol

  /** ========== Optimized Drawer States ========== */
  const { 
    drawers, 
    modals, 
    toggleDrawer, 
    toggleModal, 
    closeAllDrawers, 
    closeAllModals 
  } = useDrawerStates()

  // Extract individual drawer states for backward compatibility
  const drawerOpen = drawers.chat
  const backtesterDrawerOpen = drawers.backtester
  const opionsBuilderDrawerOpen = drawers.optionsBuilder
  const eventsDrawerOpen = drawers.events
  const sentimentDrawerOpen = drawers.sentiment
  const hedgingDrawerOpen = drawers.hedging
  const historicalsDrawerOpen = drawers.historicals
  const aiHedgeFundDrawerOpen = drawers.aiHedgeFund
  const basketsDrawerOpen = drawers.baskets
  const widgetDrawerOpen = drawers.widget
  const automationsModalOpen = modals.automations

  // Wrap toggle functions in useCallback for stable references
  const handleChatClick = useCallback(() => toggleDrawer('chat'), [toggleDrawer])
  const handleOverlayClick = useCallback(() => toggleDrawer('chat'), [toggleDrawer])

  /** ========== Optimized Layout Persistence ========== */
  const { layout, widgetMap, updateLayout, updateWidgetMap } = useLayoutPersistence()
  
  // Use local state for layout with fallback to persisted layout
  const [currentLayout, setCurrentLayout] = useState<RGLLayout>(layout || defaultLayout)
  const [currentWidgetMap, setCurrentWidgetMap] = useState<Record<string, string>>(widgetMap || {})

  // Update local state when persisted data changes
  useEffect(() => {
    if (layout) setCurrentLayout(layout)
  }, [layout])

  useEffect(() => {
    if (widgetMap) setCurrentWidgetMap(widgetMap)
  }, [widgetMap])

  // Layout persistence is now handled by React Query hooks - no useEffect needed!

  /** ========== Chart Data Fetch ========== */
  // Use React Query for optimized chart data fetching with caching
  const { data: chartData = [], isLoading: chartLoading, error: chartError } = useChartData(symbol)

  // Note: Loading and error states will be handled in the final JSX return to avoid Rules of Hooks violation

  /** ========== Optimized Grid Management ========== */
  const containerRef = useRef<HTMLDivElement>(null)
  const { gridWidth } = useGridManagement(containerRef)

  /** ========== Optimized Event Listeners ========== */
  const drawerCallbacks = useMemo(() => ({
    widget: () => toggleDrawer('widget'),
    backtester: () => toggleDrawer('backtester'),
    optionsBuilder: () => toggleDrawer('optionsBuilder'),
    events: () => toggleDrawer('events'),
    sentiment: () => toggleDrawer('sentiment'),
    hedging: () => toggleDrawer('hedging'),
    historicals: () => toggleDrawer('historicals'),
    aiHedgeFund: () => toggleDrawer('aiHedgeFund'),
    baskets: () => toggleDrawer('baskets'),
    automations: () => toggleModal('automations'),
  }), [toggleDrawer, toggleModal])

  useEventListeners(drawerCallbacks)

  /** ========== Toggle Widget Drawer ========== */
  const toggleWidgetDrawer = useCallback(() => toggleDrawer('widget'), [toggleDrawer])

  /** ========== RGL: onDrop callback ========== */
  const onDrop = (
    currentLayout: RGLLayout,
    droppedItem: RGLLayoutItem,
    e: MouseEvent
  ) => {
    const newKey = `new-item-${Date.now()}`

    // RGL automatically appended droppedItem to currentLayout
    // We'll rename its key:
    const updatedLayout = currentLayout.map(item =>
      item === droppedItem ? { ...item, i: newKey } : item
    )
    setCurrentLayout(updatedLayout)
    updateLayout(updatedLayout)

    // Assign the dragged widget type:
    if (dragType) {
      const newWidgetMap = {
        ...currentWidgetMap,
        [newKey]: dragType
      }
      setCurrentWidgetMap(newWidgetMap)
      updateWidgetMap(newWidgetMap)
    }
    // Clear the global "dragType"
    setDragType(null)

    // (ADDED) Done dropping external widget => hide dustbin
    setIsDragging(false)
  }

  /** ========== Optimized Drag and Drop Management ========== */
  const {
    dragState,
    handleDragStart,
    handleDragEnd,
    setActiveWidget,
    updateDragState,
  } = useDragAndDrop()

  // Extract drag state for backward compatibility
  const { isDragging, draggedItemKey, dragType, activeWidgetKey } = dragState
  const setIsDragging = useCallback((value: boolean) => updateDragState({ isDragging: value }), [updateDragState])
  const setDraggedItemKey = useCallback((value: string | null) => updateDragState({ draggedItemKey: value }), [updateDragState])
  const setDragType = useCallback((value: string | null) => updateDragState({ dragType: value }), [updateDragState])
  const setActiveWidgetKey = useCallback((value: string | null) => setActiveWidget(value), [setActiveWidget])

  // Use React Query for optimized user script persistence
  const { userScriptCode, updateUserScript, clearUserScript } = useUserScript()

  // Effect to clear user script when the symbol changes
  useEffect(() => {
    // This effect runs when the symbol changes.
    // Calling clearUserScript will reset the userScriptCode,
    // which in turn should cause the useUserScriptIndicator hook
    // in AdvancedChart to remove any plotted custom indicators.
    clearUserScript()
  }, [symbol, clearUserScript]) // Dependencies: symbol and the clearUserScript function itself

  /** ========== Optimized Click Outside Handler ========== */
  useEffect(() => {
    // Only add the listener if a widget is active
    if (activeWidgetKey) {
      const handleClickOutside = (e: MouseEvent) => {
        // Check if the click was outside the active widget's drag handle
        const dragHandles = document.querySelectorAll('.myDragHandle');
        let clickedOnDragHandle = false;
        
        dragHandles.forEach(handle => {
          if (handle.contains(e.target as Node)) {
            clickedOnDragHandle = true;
          }
        });
        
        if (!clickedOnDragHandle) {
          setActiveWidgetKey(null);
        }
      };
      
      // Add the event listener
      document.addEventListener('mousedown', handleClickOutside);
      
      // Clean up
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [activeWidgetKey, setActiveWidgetKey]); // Re-run effect when activeWidgetKey changes

  // For demonstration, we create a random userId.
  // In a real app, replace this with your authentication/user management.
  const userId = `user-${Math.floor(Math.random() * 10000)}`
  const userName = 'User' // Replace with your actual username

  // Handler for UI actions received from other clients.
  const handleUIAction = (action: string, payload: any) => {
    if (action === 'changeSymbol') {
      console.log('Received changeSymbol action:', payload)
      updateSymbol(payload)
    }
  }

  return (
    // <Presence
    //   roomId={symbol} // Use symbol as room ID
    //   userId={userId} // Generate random user ID (replace with actual user ID)
    //   userName={userName} // Replace with actual username
    // >
    <TradingPageWrapper symbol={symbol}>
      <div ref={containerRef}>
      {/* Handle loading and error states for chart data */}
      {chartLoading && (
        <div className="flex items-center justify-center h-screen">
          <div className="text-gray-500">Loading chart data...</div>
        </div>
      )}
      
      {chartError && (
        <div className="flex items-center justify-center h-screen">
          <div className="text-red-500">
            Error loading chart: {chartError.message}
          </div>
        </div>
      )}
      
      {/* Only render the main content when chart data is loaded successfully */}
      {!chartLoading && !chartError && (
        <>
      {/* Add the OnboardingTutorial component */}
      <OnboardingTutorial />
      
      {/* ========== Automations Modal ========== */}
      <AutomationsModal 
        isOpen={automationsModalOpen} 
        onClose={() => toggleModal('automations')} 
      />

      {/* ========== MAIN GRID ========== */}
      <GridLayout
        className="layout"
        layout={currentLayout}
        onDrop={onDrop}
        isDroppable={true}
        droppingItem={{ i: 'widget', w: 3, h: 6 }}
        cols={12}
        rowHeight={30}
        width={gridWidth}
        margin={[7, 7]}
        containerPadding={[7, 7]}
        isDraggable={true}
        isResizable={true}
        autoSize={true}
        transformScale={1}
        /** ---- The Key: We use .myDragHandle as the handle ---- */
        draggableHandle=".myDragHandle"
        useCSSTransforms={true}
        /** ========== DRAG-TO-DELETE Callbacks ========== */
        onDragStart={(layout, oldItem) => {
          setIsDragging(true)
          setDraggedItemKey(oldItem.i)
        }}
        onDragStop={(layout, oldItem, newItem, placeholder, e) => {
          setIsDragging(false)

          // Check if dropped over dustbin:
            const dustbinEl = document.getElementById('dustbin')
            if (dustbinEl) {
              const dustbinRect = dustbinEl.getBoundingClientRect()
              const x = e.clientX
              const y = e.clientY

              if (
                x >= dustbinRect.left &&
                x <= dustbinRect.right &&
                y >= dustbinRect.top &&
                y <= dustbinRect.bottom
              ) {
                // Remove the dragged item from layout
                const newLayout = currentLayout.filter(li => li.i !== oldItem.i)
                setCurrentLayout(newLayout)
                updateLayout(newLayout)
              }
            }
          setDraggedItemKey(null)
        }}
        onLayoutChange={updatedLayout => {
          setCurrentLayout(updatedLayout)
          updateLayout(updatedLayout)
        }}
      >
        {currentLayout.map(item => {
          // If there's an entry in widgetMap, it's a new/dynamic item
          const dynamicWidget = currentWidgetMap[item.i]

          return (
            <div key={item.i} className={styles.dynamicItemContainer}>
              {/* 
              1) "Drag Handle" bar at the top
              => same style as in your working snippet
            */}
              <div
                className="myDragHandle"
                style={{
                  background: '#88a1ac12',
                  // color: '#fff',
                  padding: '6px',
                  cursor: 'move',
                  position: 'absolute',
                  top: 0,
                  right: 0,
                  zIndex: 999
                }}
                onClick={(e) => {
                  e.stopPropagation(); // Prevent triggering parent click handlers
                  // Toggle active widget key
                  setActiveWidgetKey(activeWidgetKey === item.i ? null : item.i);
                }}
              >
                {/* We keep your "☰" icon so UI stays the same! */}☰
              </div>
              {/* Dustbin icon below the drag handle - only visible when this widget is active */}
              {activeWidgetKey === item.i && (
                <div
                  className={styles.widgetDeleteButton}
                  onMouseDown={(e) => {
                    // Using onMouseDown instead of onClick for more reliable handling
                    e.stopPropagation(); // Prevent triggering parent click handlers
                    e.preventDefault(); // Prevent default behavior
                    
                    // Check if this is a script editor widget - clear script if so
                    if (dynamicWidget === 'scriptEditor') {
                      clearUserScript() // Clear the script when script editor is deleted
                    }
                    
                    // Remove this item from layout and from widgetMap if it exists there
                    console.log('Removing widget:', item.i);
                    const newLayout = currentLayout.filter(li => li.i !== item.i);
                    const newWidgetMap = {...currentWidgetMap};
                    delete newWidgetMap[item.i];
                    
                    setCurrentLayout(newLayout);
                    setCurrentWidgetMap(newWidgetMap);
                    updateLayout(newLayout);
                    updateWidgetMap(newWidgetMap);
                    setActiveWidgetKey(null); // Reset active widget
                  }}
                  title="Remove widget"
                >
                  <span style={{ fontSize: '18px' }}>🗑</span>
                </div>
              )}

              {dynamicWidget ? (
                <>
                  {dynamicWidget === 'chart' && (
                    <AdvancedChart
                      data={chartData}
                      symbol={symbol}
                      userScriptCode={userScriptCode}
                    />
                  )}
                  {dynamicWidget === 'orderBook' && <OrderBookComponent />}
                  {dynamicWidget === 'watchlist' && <WatchlistComponent />}
                  {dynamicWidget === 'buySell' && <BuySellComponent symbol={symbol} />}
                  {dynamicWidget === 'tabs' && <PluginTabs />}
                  {dynamicWidget === 'scriptEditor' && (
                    // The script editor. On "Apply," we store code in userScriptCode.
                    <ScriptEditor
                      onApply={scriptCode => updateUserScript(scriptCode)}
                    />
                  )}
                  {dynamicWidget === 'notes' && <NotesPage />}
                  {dynamicWidget === 'visualDemo' && <VisualDemo />}
                </>
              ) : (
                /** Otherwise, it's one of your default items by key */
                <>
                  {item.i === 'chartArea' && (
                    <AdvancedChart
                      data={chartData}
                      symbol={symbol}
                      userScriptCode={userScriptCode}
                    />
                  )}
                  {item.i === 'tabsAreaTop' && <PluginTabs />}
                  {item.i === 'orderBook' && <OrderBookComponent />}
                  {item.i === 'watchlist' && <WatchlistComponent />}
                  {item.i === 'buySell' && <BuySellComponent symbol={symbol} />}
                  {item.i === 'tabsAreaBottom' && <TerminalTabsComponent />}
                  {item.i === 'notes' && <NotesPage />}
                </>
              )}
            </div>
          )
        })}
      </GridLayout>

      {/* ========== Dustbin (only visible while dragging) ========== */}
      {isDragging && (
        <div
          id="dustbin"
          style={{
            position: 'fixed',
            bottom: 40,
            left: '50%',
            transform: 'translateX(-50%)',
            width: draggedItemKey ? 120 : 80,  // Expand when item is being dragged
            height: draggedItemKey ? 120 : 80,
            borderRadius: '50%',
            backgroundColor: draggedItemKey ? 'rgba(255,0,0,0.3)' : 'rgba(255,0,0,0.2)',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 99999,
            transition: 'all 0.3s ease-in-out',
            boxShadow: draggedItemKey ? '0 0 20px rgba(255,0,0,0.3)' : 'none',
            cursor: 'pointer'
          }}
        >
          <span style={{ 
            fontSize: draggedItemKey ? 36 : 28,
            transition: 'all 0.3s ease-in-out'
          }}>🗑</span>
          <span style={{
            marginTop: '8px',
            fontSize: '14px',
            color: '#fff',
            opacity: 0.8
          }}>
            Drop to Remove
          </span>
        </div>
      )}

      {/* ========== Additional TABS Below ========== */}
      <div className={styles.terminalTabs}>
        {/* Use the broker selection hook to dynamically select the appropriate TerminalTabs component */}
        <TerminalTabsComponent />
      </div>

      {/* ========== FLOATING CHAT BUTTON ========== */}
      <button 
        className={styles.chatButton} 
        onClick={handleChatClick}
        data-tutorial="stockbot-chat-button"
      >
        <Image
          src="/chat-ai.svg"
          alt="NINtrade Stockbot Icon"
          width={28}
          height={28}
          priority={true}
        />
      </button>
      {drawerOpen && (
        <div className={styles.overlay} onClick={handleOverlayClick}></div>
      )}
      <div
        className={`${styles.drawer} ${drawerOpen ? styles.drawerOpen : ''}`}
      >
        <iframe
          src="https://stockbot.nin.in/"
          className={styles.iframeContainer}
          title="StockBot Chat"
        ></iframe>
      </div>

      {/* ========== "Backtester" drawer ========== */}
      {backtesterDrawerOpen && (
        // An overlay that closes the Backtester drawer when clicked
        <div
          className={styles.overlay}
          onClick={() => toggleDrawer('backtester')}
        ></div>
      )}
      <div
        className={`${styles.drawer} ${backtesterDrawerOpen ? styles.drawerOpen : ''}`}
      >
        {/* Render the Backtester component inside the drawer */}
        <Backtester ticker={symbol.toUpperCase()} />
      </div>

      {/* ========== "Plugins" drawer ========== */}
      {opionsBuilderDrawerOpen && (
        // An overlay that closes the Backtester drawer when clicked
        <div
          className={styles.overlay}
          onClick={() => toggleDrawer('optionsBuilder')}
        ></div>
      )}
      <div
        className={`${styles.drawer} ${opionsBuilderDrawerOpen ? styles.drawerOpen : ''}`}
      >
        {/* Render the Backtester component inside the drawer */}
        <OptionsBuilder />
      </div>

      {/* ========== "Plugins" drawer ========== */}
      {eventsDrawerOpen && (
        // An overlay that closes the Events drawer when clicked
        <div
          className={styles.overlay}
          onClick={() => toggleDrawer('events')}
        ></div>
      )}
      <div
        className={`${styles.drawer} ${eventsDrawerOpen ? styles.drawerOpen : ''}`}
      >
        {/* Render the Events component inside the drawer */}
        <Events />
      </div>

      {/* ========== "Plugins" drawer ========== */}
      {sentimentDrawerOpen && (
        // An overlay that closes the Events drawer when clicked
        <div
          className={styles.overlay}
          onClick={() => toggleDrawer('sentiment')}
        ></div>
      )}
      <div
        className={`${styles.drawer} ${sentimentDrawerOpen ? styles.drawerOpen : ''}`}
      >
        {/* Render the Events component inside the drawer */}
        <Sentiment />
      </div>

      {/* ========== "Plugins" drawer ========== */}
      {hedgingDrawerOpen && (
        // An overlay that closes the Events drawer when clicked
        <div
          className={styles.overlay}
          onClick={() => toggleDrawer('hedging')}
        ></div>
      )}
      <div
        className={`${styles.drawer} ${hedgingDrawerOpen ? styles.drawerOpen : ''}`}
      >
        {/* Render the Events component inside the drawer */}
        <Hedging />
      </div>

      {/* ========== Historicals Drawer ========== */}
      {historicalsDrawerOpen && (
        // An overlay that closes the Historicals drawer when clicked
        <div
          className={styles.overlay}
          onClick={() => toggleDrawer('historicals')}
        ></div>
      )}
      <div
        className={`${styles.drawer} ${historicalsDrawerOpen ? styles.drawerOpen : ''}`}
      >
        {/* Render the Historicals component inside the drawer */}
        <Historicals />
      </div>

      {/* ========== AI HedgeFund drawer ========== */}
      {aiHedgeFundDrawerOpen && (
        <>
          <div className={styles.overlay} 
          onClick={() => toggleDrawer('aiHedgeFund')}>
          </div>
          <div className={`${styles.drawer} ${styles.drawerOpen}`}>
            {/* Only render the AIHedgeFund component when the drawer is open */}
            <AIHedgeFund ticker={symbol} />
          </div>
        </>
      )}

      {/* ========== Baskets drawer ========== */}
      {basketsDrawerOpen && (
        <div className={styles.overlay} 
        onClick={() => toggleDrawer('baskets')}>
        </div>
      )}
      <div className={`${styles.drawer} ${basketsDrawerOpen ? styles.drawerOpen : ''}`}>
        {/* Always render the Baskets component to preserve state for localstorage values */}
        <Baskets />
      </div>

      {/* ========== Automations Modal ========== */}
      <AutomationsModal 
        isOpen={automationsModalOpen} 
        onClose={() => toggleModal('automations')} 
      />

      {/* ========== "Widget" drawer ========== */}
      {/* <button className={styles.addWidgetButton} onClick={toggleWidgetDrawer}>
      Add Widget
    </button> */}

      <div
        className={`
          ${styles.widgetDrawer} 
          ${widgetDrawerOpen ? styles.widgetDrawerOpen : ''} 
          ${permanentMarker.className}
        `}
      >
        {/* Drawer Header with Close Button */}
        <div className={styles.widgetDrawerHeader}>
          <h3>Widgets</h3>
          <button className={styles.closeButton} onClick={toggleWidgetDrawer}>
            ✕
          </button>
        </div>
        <p>Drag an item below onto the grid:</p>

        <div
          className={styles.widgetDraggable}
          draggable
          onDragStart={() => handleDragStart('chart')}
        >
          <Image
            src="/chart_drag.webp"
            alt="Chart Widget"
            width={100}
            height={60}
            style={{ borderRadius: 12 }}
          />
          <span>Chart</span>
        </div>

        <div
          className={styles.widgetDraggable}
          draggable
          onDragStart={() => handleDragStart('orderBook')}
        >
          <Image
            src="/orderbook_drag.webp"
            alt="OrderBook Widget"
            width={100}
            height={60}
            style={{ borderRadius: 12 }}
          />
          <span>OrderBook</span>
        </div>

        <div
          className={styles.widgetDraggable}
          draggable
          onDragStart={() => handleDragStart('watchlist')}
        >
          <Image
            src="/watchlist_drag.webp"
            alt="Watchlist Widget"
            width={100}
            height={60}
            style={{ borderRadius: 12, fontFamily: 'Atomic Marker, cursive' }}
          />
          <span>Watchlist</span>
        </div>

        <div
          className={styles.widgetDraggable}
          draggable
          onDragStart={() => handleDragStart('buySell')}
        >
          <Image
            src="/buysell_drag.webp"
            alt="BuySell Widget"
            width={100}
            height={60}
            style={{ borderRadius: 12 }}
          />
          <span>Buy/Sell</span>
        </div>

        <div
          className={styles.widgetDraggable}
          draggable
          onDragStart={() => handleDragStart('tabs')}
        >
          <Image
            src="/pluginstabs_drag.webp"
            alt="Tabs Widget"
            width={100}
            height={60}
            style={{ borderRadius: 12 }}
          />
          <span>Plugin Tabs</span>
        </div>

        <div
          className={styles.widgetDraggable}
          draggable
          onDragStart={() => handleDragStart('scriptEditor')}
        >
          <Image
            src="/indicatorscript_drag.webp"
            alt="Indicator Editor"
            width={100}
            height={60}
            style={{ borderRadius: 12 }}
          />
          <span>Indicator Editor</span>
        </div>

        <div
          className={styles.widgetDraggable}
          draggable
          onDragStart={() => handleDragStart('notes')}
        >
          <Image
            src="/notes_drag.webp"
            alt="Notes"
            width={100}
            height={60}
            style={{ borderRadius: 12 }}
          />
          <span>Notes</span>
        </div>

        <div
          className={styles.widgetDraggable}
          draggable
          onDragStart={() => handleDragStart('visualDemo')}
        >
          <div style={{
            width: '100px',
            height: '60px',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            borderRadius: '12px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '24px'
          }}>
            🎬
          </div>
          <span>AI Visual Demo</span>
        </div>
      </div>
    </>
      )}
    </div>
    </TradingPageWrapper>
    // </Presence>
  )
}
